/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

export type TooltipSide = 'top' | 'right' | 'bottom' | 'left';
export type TooltipAlign = 'center' | 'start' | 'end';

export interface TooltipPreferences {
  /** Logical, as the `direction` input: in a right-to-left page `right` is the trigger's inline end, its left. */
  side: TooltipSide;
  align: TooltipAlign;
}

export interface TooltipRect {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface TooltipSize {
  width: number;
  height: number;
}

export interface TooltipPlacement {
  top: number;
  left: number;
  /** The side it ended up on, physical. */
  side: TooltipSide;
}

/** Between the bubble and its trigger. */
const GAP = 8;
/** Between the bubble and the edge of the window. */
const MARGIN = 8;

const OPPOSITE: Record<TooltipSide, TooltipSide> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

function isVertical(side: TooltipSide): boolean {
  return side === 'top' || side === 'bottom';
}

/** Moves a box of `size` starting at `start` into the window, `extent` long, leaving the margin on both sides. */
function clamp(start: number, size: number, extent: number): number {
  return Math.max(MARGIN, Math.min(start, extent - MARGIN - size));
}

/**
 * Where to put a bubble of `bubble` size next to `anchor`, all in viewport pixels.
 *
 * It opens on the preferred side if it fits there, else on the opposite one. A `left` or `right` preference also
 * tries above, then below, which is where the room is on a narrow screen. When nothing fits, it takes the roomier
 * of the preferred side and its opposite. Along the trigger it starts from `align` (a `left` or `right` bubble is
 * always centred) and then shifts as far as it must to stay inside the window.
 */
export function placeTooltip(
  anchor: TooltipRect,
  bubble: TooltipSize,
  viewport: TooltipSize,
  { side, align, rtl }: TooltipPreferences & { rtl: boolean },
): TooltipPlacement {
  const preferred = rtl && !isVertical(side) ? OPPOSITE[side] : side;
  const lineUp = rtl && align !== 'center' ? (align === 'start' ? 'end' : 'start') : align;

  const room: Record<TooltipSide, number> = {
    top: anchor.top - GAP - MARGIN,
    bottom: viewport.height - MARGIN - GAP - anchor.bottom,
    left: anchor.left - GAP - MARGIN,
    right: viewport.width - MARGIN - GAP - anchor.right,
  };
  const fits = (candidate: TooltipSide) => room[candidate] >= (isVertical(candidate) ? bubble.height : bubble.width);
  const candidates: TooltipSide[] = isVertical(preferred)
    ? [preferred, OPPOSITE[preferred]]
    : [preferred, OPPOSITE[preferred], 'top', 'bottom'];
  const chosen = candidates.find(fits) ?? (room[preferred] >= room[OPPOSITE[preferred]] ? preferred : OPPOSITE[preferred]);

  if (isVertical(chosen)) {
    const top = chosen === 'top' ? anchor.top - GAP - bubble.height : anchor.bottom + GAP;
    const left =
      lineUp === 'start' ? anchor.left : lineUp === 'end' ? anchor.right - bubble.width : (anchor.left + anchor.right - bubble.width) / 2;
    return {
      top: clamp(top, bubble.height, viewport.height),
      left: clamp(left, bubble.width, viewport.width),
      side: chosen,
    };
  }
  const left = chosen === 'left' ? anchor.left - GAP - bubble.width : anchor.right + GAP;
  const top = (anchor.top + anchor.bottom - bubble.height) / 2;
  return {
    top: clamp(top, bubble.height, viewport.height),
    left: clamp(left, bubble.width, viewport.width),
    side: chosen,
  };
}

/**
 * Raises the bubble into the top layer, where no ancestor's `overflow`, container or `z-index` can clip or cover it,
 * and places it next to its trigger. It stays on one line when that fits the window (or the `multiline` cap), and is
 * marked `wrapped` otherwise, which puts each part on its own line.
 */
export function positionTooltip(bubble: HTMLElement, trigger: HTMLElement, preferences: TooltipPreferences): void {
  // A popover is not displayed, so cannot be measured, until it is shown. jsdom has no popovers, and a bubble in a
  // view not yet attached cannot be shown; either way it stays where its styles put it.
  if (typeof bubble.showPopover === 'function' && bubble.isConnected && !bubble.matches(':popover-open')) {
    bubble.showPopover();
  }
  bubble.classList.remove('wrapped');
  if (bubble.scrollWidth > bubble.clientWidth) {
    bubble.classList.add('wrapped');
  }

  const { width, height } = bubble.getBoundingClientRect();
  const root = bubble.ownerDocument.documentElement;
  const viewport = { width: root.clientWidth, height: root.clientHeight };
  const rtl = bubble.ownerDocument.defaultView?.getComputedStyle(trigger).direction === 'rtl';
  const placement = placeTooltip(trigger.getBoundingClientRect(), { width, height }, viewport, { ...preferences, rtl });

  bubble.style.top = `${Math.round(placement.top)}px`;
  bubble.style.left = `${Math.round(placement.left)}px`;
  bubble.dataset['side'] = placement.side;
}

/**
 * Keeps a bubble with its trigger: `reposition` runs on every scroll and resize, and the bubble hides while the
 * trigger is scrolled out of sight, so it never floats on its own. Returns the function that stops this.
 */
export function trackTooltip(bubble: HTMLElement, trigger: HTMLElement, reposition: () => void): () => void {
  const view = bubble.ownerDocument.defaultView;
  if (!view) {
    return () => undefined;
  }
  view.addEventListener('scroll', reposition, { capture: true, passive: true });
  view.addEventListener('resize', reposition, { passive: true });

  // Unlike the window's bounds, this also knows when a scroll container has hidden the trigger.
  let observer: IntersectionObserver | undefined;
  if (typeof view.IntersectionObserver === 'function') {
    observer = new view.IntersectionObserver((entries) =>
      bubble.classList.toggle('out-of-view', !entries[entries.length - 1].isIntersecting),
    );
    observer.observe(trigger);
  }

  return () => {
    view.removeEventListener('scroll', reposition, { capture: true });
    view.removeEventListener('resize', reposition);
    observer?.disconnect();
  };
}
